import { useState, useEffect, useRef, useCallback } from 'react';
import { ALL_VIDEO_KINDS, parseVideoEvent, type VideoData } from '@/lib/videoTypes';
import type { NostrEvent, NostrFilter } from '@nostrify/nostrify';
import { useAppContext } from '@/hooks/useAppContext';
import { getRelay } from '@/lib/relayPool';

const INITIAL_LIMIT = 60;
const PAGE_LIMIT = 60;
/** Max time a relay has to deliver its initial batch before we stop waiting. */
const EOSE_TIMEOUT_MS = 10000;
/** Timeout for one-shot "load more" paginated queries per relay. */
const PAGE_TIMEOUT_MS = 8000;
/** Cap on accumulated videos kept in memory. */
const MAX_BUFFERED = 800;
/** Minimum interval between state flushes while events stream in. */
const FLUSH_INTERVAL_MS = 200;

export interface VideoStreamQuery {
  /** Video kinds to fetch (defaults to all NIP-71 kinds). */
  kinds?: number[];
  /** Restrict to these authors. Empty array disables the query. */
  authors?: string[];
  /** Restrict to this hashtag (t tag). */
  tag?: string;
  /** NIP-50 full-text search string (supported relays only). */
  search?: string;
  /**
   * Per-relay event limit for each batch (default 60). Raise it for
   * client-side-filtered kinds like kind 1, where most events are not videos.
   */
  limit?: number;
  /** Set false to pause the stream. */
  enabled?: boolean;
}

export interface VideoStream {
  videos: VideoData[];
  /** True until the first batch has settled (or the first event arrived). */
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
}

function dedupe(videos: VideoData[]): VideoData[] {
  // For addressable events, only keep the newest version per pubkey+kind+d
  const byAddress = new Map<string, VideoData>();
  const regular: VideoData[] = [];

  for (const v of videos) {
    if (v.dTag && (v.originalEvent.kind === 34235 || v.originalEvent.kind === 34236)) {
      const key = `${v.originalEvent.kind}:${v.pubkey}:${v.dTag}`;
      const existing = byAddress.get(key);
      if (!existing || v.createdAt > existing.createdAt) {
        byAddress.set(key, v);
      }
    } else {
      regular.push(v);
    }
  }

  return [...regular, ...byAddress.values()].sort((a, b) => b.createdAt - a.createdAt);
}

/**
 * Stream video events from all read relays simultaneously.
 *
 * Unlike a single pooled query (which resolves when the slowest relay
 * answers or a fixed timeout hits), this hook opens one streaming
 * subscription per relay and paints results incrementally as each relay
 * responds — the UI is usable immediately while events keep arriving in
 * realtime. After EOSE each subscription stays open for live updates.
 */
export function useVideoStream(query: VideoStreamQuery): VideoStream {
  const { config } = useAppContext();
  const [videos, setVideos] = useState<VideoData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const accRef = useRef(new Map<string, VideoData>());
  const cursorRef = useRef<number>(Math.floor(Date.now() / 1000));
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const kindsKey = (query.kinds ?? ALL_VIDEO_KINDS).join(',');
  const enabled = query.enabled !== false;
  const authorsKey = query.authors ? query.authors.slice().sort().join(',') : '';
  const hasEmptyAuthors = query.authors !== undefined && query.authors.length === 0;

  const readRelays = config.relayMetadata.relays.filter(r => r.read).map(r => r.url);
  const relayKey = readRelays.join(',');

  /**
   * Build ONE FILTER PER KIND. NIP-01 applies `limit` per filter object
   * within a REQ, so combining kinds under a single filter lets high-volume
   * kinds (e.g. addressable 34235/34236 from DiVine, import bots, or
   * triple-posting clients) squeeze rarer kinds (regular 21/22) out of the
   * result window entirely. Separate per-kind filters give every kind its
   * own budget.
   */
  const buildFilters = useCallback((until?: number): NostrFilter[] => {
    const batchLimit = query.limit ?? (until ? PAGE_LIMIT : INITIAL_LIMIT);
    const kinds = query.kinds ?? ALL_VIDEO_KINDS;

    return kinds.map((kind) => {
      const filter: NostrFilter = {
        kinds: [kind],
        limit: batchLimit,
      };
      if (query.authors && query.authors.length > 0) filter.authors = query.authors;
      if (query.tag) filter['#t'] = [query.tag.toLowerCase()];
      if (query.search) filter.search = query.search;
      if (until) filter.until = until;
      return filter;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindsKey, authorsKey, query.tag, query.search, query.limit]);

  /** Throttled flush of the accumulator into React state. */
  const scheduleFlush = useCallback(() => {
    if (flushTimerRef.current) return;
    flushTimerRef.current = setTimeout(() => {
      flushTimerRef.current = null;
      setVideos(dedupe(Array.from(accRef.current.values())).slice(0, MAX_BUFFERED));
    }, FLUSH_INTERVAL_MS);
  }, []);

  const addEvents = useCallback((events: NostrEvent[]) => {
    let added = 0;
    for (const e of events) {
      const v = parseVideoEvent(e);
      if (v && !accRef.current.has(v.id)) {
        accRef.current.set(v.id, v);
        if (v.createdAt < cursorRef.current) cursorRef.current = v.createdAt;
        added++;
      }
    }
    if (added > 0) scheduleFlush();
    return added;
  }, [scheduleFlush]);

  // ── Live streaming phase ────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled || hasEmptyAuthors || readRelays.length === 0) {
      accRef.current = new Map();
      setVideos([]);
      setIsLoading(false);
      setHasMore(false);
      return;
    }

    accRef.current = new Map();
    cursorRef.current = Math.floor(Date.now() / 1000);
    setVideos([]);
    setIsLoading(true);
    setHasMore(true);

    const controller = new AbortController();
    const total = readRelays.length;
    let settledCount = 0;
    let sawAnyEvent = false;
    let loadingDone = false;

    const markSettled = () => {
      settledCount++;
      if (settledCount >= total && !loadingDone) {
        loadingDone = true;
        setIsLoading(false);
      }
    };

    const filters = buildFilters();

    for (const url of readRelays) {
      const relay = getRelay(url);
      let eosed = false;
      // If a relay never sends EOSE, stop waiting on it after a while.
      const eoseTimer = setTimeout(() => {
        if (!eosed) {
          eosed = true;
          markSettled();
        }
      }, EOSE_TIMEOUT_MS);

      (async () => {
        try {
          for await (const msg of relay.req(filters, { signal: controller.signal })) {
            if (msg[0] === 'EOSE') {
              if (!eosed) {
                eosed = true;
                clearTimeout(eoseTimer);
                markSettled();
              }
              // Keep iterating after EOSE — live events keep streaming in
              // until the effect is torn down.
            } else if (msg[0] === 'EVENT') {
              const event = msg[2];
              if (!sawAnyEvent) {
                sawAnyEvent = true;
                if (!loadingDone) {
                  loadingDone = true;
                  setIsLoading(false);
                }
              }
              addEvents([event]);
            }
          }
        } catch {
          // Relay failed or aborted — other relays continue.
        } finally {
          clearTimeout(eoseTimer);
          if (!eosed) {
            eosed = true;
            markSettled();
          }
        }
      })();
    }

    // Safety net: never stay in the loading state forever
    const loadingTimeout = setTimeout(() => {
      if (!loadingDone) {
        loadingDone = true;
        setIsLoading(false);
      }
    }, EOSE_TIMEOUT_MS + 1000);

    return () => {
      clearTimeout(loadingTimeout);
      controller.abort();
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relayKey, kindsKey, authorsKey, query.tag, query.search, query.limit, enabled]);

  // ── Cursor-based pagination ─────────────────────────────────────────────
  const loadMore = useCallback(() => {
    if (isLoadingMore || isLoading || !hasMore || hasEmptyAuthors) return;

    setIsLoadingMore(true);
    const until = cursorRef.current - 1;
    const filters = buildFilters(until);

    let remaining = readRelays.length;
    let added = 0;

    if (remaining === 0) {
      setIsLoadingMore(false);
      setHasMore(false);
      return;
    }

    for (const url of readRelays) {
      const relay = getRelay(url);
      relay.query(filters, { signal: AbortSignal.timeout(PAGE_TIMEOUT_MS) })
        .then((events) => {
          added += addEvents(events);
        })
        .catch(() => {})
        .finally(() => {
          remaining--;
          if (remaining === 0) {
            setIsLoadingMore(false);
            if (added === 0) setHasMore(false);
          }
        });
    }
  }, [isLoadingMore, isLoading, hasMore, hasEmptyAuthors, buildFilters, readRelays, addEvents]);

  return { videos, isLoading, isLoadingMore, hasMore, loadMore };
}
